import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import EquipmentService from "../app/data/equipment/equipmentServices";
import TerminalService from "../app/data/terminal-summary/terminalsummaryService";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import { EquipmentLogFilterModel } from "app/data/equipment/models";

const equipmentService = EquipmentService.getInstance();
const terminalService = TerminalService.getInstance();

interface DocumentToUpload {
  fileType: 'REGISTRATION' | 'DOT_INSPECTION';
  file: File;
}

interface UploadFilePayload {
  equipmentId?: string;
  fileType: string;
  file: File;
}

const getCreatedEquipmentId = (createResponse: unknown): string | undefined => {
  if (!createResponse || typeof createResponse !== "object") {
    return undefined;
  }

  const response = createResponse as Record<string, unknown>;
  const nestedData = response.data;

  if (typeof response.id === "string" && response.id.trim()) {
    return response.id.trim();
  }

  if (typeof response.id === "number") {
    return String(response.id);
  }

  if (nestedData && typeof nestedData === "object") {
    const nestedId = (nestedData as Record<string, unknown>).id;
    if (typeof nestedId === "string" && nestedId.trim()) {
      return nestedId.trim();
    }
    if (typeof nestedId === "number") {
      return String(nestedId);
    }
  }

  return undefined;
};

const resolveEquipmentIdAfterCreate = async (
  createResponse: unknown,
  payload: { unitNumber?: string | null }
): Promise<string | undefined> => {
  const directId = getCreatedEquipmentId(createResponse);
  if (directId) {
    return directId;
  }

  const unitNumber = payload.unitNumber?.trim();
  if (!unitNumber) {
    return undefined;
  }

  const listPage = await equipmentService.getEquipmentList(unitNumber, 0);
  const match = listPage?.content?.find(
    (item) => item.unitNumber?.trim() === unitNumber
  );

  return match?.id?.trim() || undefined;
};

export interface EquipmentTerminalOption {
  label: string;
  value: string;
  airportCode: string;
  terminalName: string;
  terminalNumber: number;
}

export interface EquipmentActivityLogFilters {
  search?: string;
  action?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

const invalidateEquipmentActivityLogs = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_ACTIVITY_LOG] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_CHANGE_LOG] });
};

export const useTerminalsList = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_TERMINALS_LIST],
    queryFn: () => terminalService.getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true),

    select: (res): EquipmentTerminalOption[] => {
      return (res?.data?.content || []).map((terminal: any) => ({
        label: terminal.terminalName,
        value: terminal.terminalName,
        airportCode: terminal.airportCode || "",
        terminalName: terminal.terminalName || "",
        terminalNumber: terminal.terminalNumber,
      }));
    },
    staleTime: 1000 * 60 * 10,
  });
};

export const useEquipmentTypes = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_EQUIPMENT_TYPES],
    queryFn: () => equipmentService.getEquipmentTypes(),
    select: (res) => {
      return (res || []).map((item: string) => ({
        label: item,
        value: item,
      }));
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useProductLines = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_PRODUCT_LINES],
    queryFn: () => equipmentService.getProductLines(),
    select: (res) => {
      return (res || []).map((item: string) => ({
        label: item,
        value: item,
      }));
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useAssetDescriptor = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_ASSET_DESCRIPTOR],
    queryFn: () => equipmentService.getAssetDescriptor(),
    select: (res) => {
      return (res || []).map((item: string) => ({
        label: item,
        value: item,
      }));
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useLeaseTypes = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_LEASE_TYPES],
    queryFn: () => equipmentService.getLeaseTypes(),
    select: (res) => {
      return (res || []).map((item: string) => ({
        label: item,
        value: item,
      }));
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useMakes = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MAKES],
    queryFn: () => equipmentService.getMakes(),
    select: (res) => {
      return (res || []).map((item: string) => ({
        label: item,
        value: item,
      }));
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useEquipmentList = (
  search?: string,
  type?: string,
  status?: string,
  terminal?: string
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_EQUIPMENT_LIST, search, type, status, terminal],
    queryFn: ({ pageParam = 0 }) =>
      equipmentService.getEquipmentList(search, pageParam, type, status, terminal),

    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.number ?? 0;
      const totalPages = lastPage?.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },

    select: (data) => {
      const allItems = data.pages.flatMap((p) => p.content);
      return { ...data, allItems };
    },

    staleTime: 1000 * 60 * 5,
  });
};


export const useEquipmentListDetails = (equipmentNumber: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_EQUIPMENT_DETAIL, equipmentNumber],
    queryFn: () => equipmentService.getEquipmentDetails(equipmentNumber),
    enabled: !!equipmentNumber,
    staleTime: 1000 * 60 * 5,
  });
};

export const useEquipmentActivityLogList = (
  filters: EquipmentActivityLogFilters,
  enabled: boolean = true
) => {
  const { search, action, dateFrom, dateTo } = filters;

  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_EQUIPMENT_ACTIVITY_LOG, search, action, dateFrom, dateTo],
    queryFn: ({ pageParam = 0 }) =>
      equipmentService.getEquipmentActivityLogList(
        pageParam,
        search,
        action,
        dateFrom,
        dateTo
      ),
    getNextPageParam: (lastPage) => {
      if (lastPage?.last) return undefined;
      const currentPage = lastPage?.pageable?.pageNumber ?? lastPage?.number ?? 0;
      return currentPage + 1;
    },
    select: (data) => {
      const allItems = data.pages.flatMap((p) => p?.content || []);
      return { ...data, allItems };
    },
    enabled,
    staleTime: 1000 * 60,
  });
};

export const useEquipmentChangeLog = (
  equipmentId: string | null | undefined,
  filter: EquipmentLogFilterModel = {},
  enabled: boolean = true
) => {
  return useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.GET_EQUIPMENT_CHANGE_LOG,
      equipmentId,
      filter.dateFrom,
      filter.dateTo,
    ],
    queryFn: ({ pageParam = 0 }) =>
      equipmentService.getEquipmentChangeLog(equipmentId as string, filter, pageParam),
    getNextPageParam: (lastPage) => {
      if (lastPage?.last) return undefined;
      const currentPage = lastPage?.pageable?.pageNumber ?? lastPage?.number ?? 0;
      return currentPage + 1;
    },
    select: (data) => {
      const allItems = data.pages.flatMap((p) => p?.content || []);
      return { ...data, allItems };
    },
    enabled: enabled && !!equipmentId,
    staleTime: 1000 * 60,
  });
};

// CREATE
export const useCreateEquipmentWithDocuments = () => {
  const queryClient = useQueryClient();
  const uploadFileMutation = useUploadEquipmentFile();

  return useMutation({
    mutationFn: async ({ payload, filesToUpload }: { payload: any, filesToUpload: DocumentToUpload[] }) => {
      const createResponse = await EquipmentService.getInstance().createEquipment(payload);

      let equipmentId = await resolveEquipmentIdAfterCreate(createResponse, payload);

      if (filesToUpload.length > 0 && !equipmentId) {
        throw new Error("Equipment was created but the ID was not returned. Unable to upload documents.");
      }

      if (equipmentId && filesToUpload.length > 0) {
        const uploadPromises = filesToUpload.map(async (doc) => {
          return await uploadFileMutation.mutateAsync({
            equipmentId,
            fileType: doc.fileType,
            file: doc.file,
          });
        });

        await Promise.all(uploadPromises);
      }

      return createResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_LIST] });
      queryClient.refetchQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_LIST] });
      invalidateEquipmentActivityLogs(queryClient);
    },
  });
};

// UPDATE
export const useUpdateEquipment = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, payload }: any) => EquipmentService.getInstance().updateEquipment(id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_LIST] });
        invalidateEquipmentActivityLogs(queryClient);
      },
      onError: (error: any) => {
        throw new Error(error.message);
      }
    }
  );
};

// DELETE
export const useDeleteEquipment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => equipmentService.deleteEquipment(id),

    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_EQUIPMENT_LIST]);
      invalidateEquipmentActivityLogs(queryClient);
    },
  });
};

export const useUploadEquipmentFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ equipmentId, fileType, file }: UploadFilePayload) => {
      if (equipmentId) {
        return equipmentService.uploadEquipmentFile(equipmentId, fileType, file);
      } else {
        return equipmentService.uploadInitialDocument(file);
      }
    },

    onSuccess: (data, variables) => {
      if (variables.equipmentId) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_DETAIL, variables.equipmentId] });
      }
    },
  });
};

export const useDeleteEquipmentFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ equipmentId, fileType }: { equipmentId: string, fileType: string }) =>
      equipmentService.deleteEquipmentFile(equipmentId, fileType),

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_DETAIL, variables.equipmentId] });
    },
  });
};
